import { api } from "@/lib/apiClient";

export const HOME_TILE_IDS = [
  "kpis",
  "todos",
  "mentions",
  "absences",
  "my-day",
  "material",
  "completions",
  "lieferscheine",
  "angebote",
  "rueckfragen",
  "billing-signed",
  "billing-lieferscheine",
  "billing-drafts",
  "billing-questions",
  "billing-missing-numbers",
  "office-points",
  "orders",
  "personal-overview",
  "personal-absences",
  "team-absences",
  "subcontractors",
  "bw-material-open",
  "bw-material-work",
  "bw-inquiries",
  "bw-orders-late",
] as const;

export type HomeTileId = (typeof HOME_TILE_IDS)[number];

export type HomeColumnCount = 1 | 2 | 3;

export type HomeTileSpan = 1 | 2 | 3;

export const HOME_TILE_GROUPS = ["Allgemein", "Personal", "Bestellwesen"] as const;

export type HomeTileGroup = (typeof HOME_TILE_GROUPS)[number];

export interface HomeTileInfo {
  label: string;
  description: string;
  group: HomeTileGroup;
}

export const HOME_TILE_INFO: Record<HomeTileId, HomeTileInfo> = {
  kpis: {
    label: "Kennzahlen",
    description: "Die wichtigsten Zahlen deiner Rolle, zum Beispiel offene Vorgänge oder Umsätze.",
    group: "Allgemein",
  },
  todos: {
    label: "To-Dos",
    description: "Eigene Aufgaben und Wiedervorlagen.",
    group: "Allgemein",
  },
  mentions: {
    label: "Erwähnungen",
    description: "Offene Erwähnungen aus Projektnotizen.",
    group: "Allgemein",
  },
  absences: {
    label: "Abwesenheitsanträge",
    description: "Urlaub und Abwesenheiten, die auf deine Freigabe warten.",
    group: "Personal",
  },
  "my-day": {
    label: "Mein Tag",
    description: "Deine Einsätze und die Unterlagen für heute.",
    group: "Allgemein",
  },
  material: {
    label: "Material bereit",
    description: "Material, das für dich bereitgelegt oder geliefert wurde.",
    group: "Bestellwesen",
  },
  completions: {
    label: "Fertigstellungsmeldungen",
    description: "Projekte mit offener Fertigstellungsmeldung.",
    group: "Allgemein",
  },
  lieferscheine: {
    label: "Lieferscheine beim Ansprechpartner",
    description: "Lieferscheine, die noch beim Ansprechpartner liegen.",
    group: "Allgemein",
  },
  angebote: {
    label: "Offene Angebotsanfragen",
    description: "Angebotsanfragen, die noch nicht erledigt sind.",
    group: "Allgemein",
  },
  rueckfragen: {
    label: "Abrechnungs-Rückfragen",
    description: "Offene Rückfragen zu Stundennachweisen und Dokumenten.",
    group: "Allgemein",
  },
  "billing-signed": {
    label: "Unterschriebene Stundennachweise",
    description: "Nachweise, die unterschrieben und bereit zur Abrechnung sind.",
    group: "Allgemein",
  },
  "billing-lieferscheine": {
    label: "Neue Lieferscheine",
    description: "Gelieferte Lieferscheine, die die Abrechnung noch nicht gesehen hat.",
    group: "Allgemein",
  },
  "billing-drafts": {
    label: "Rechnungsentwürfe",
    description: "Stundennachweise, zu denen ein Rechnungsentwurf vorliegt.",
    group: "Allgemein",
  },
  "billing-questions": {
    label: "Nachweise mit Rückfrage",
    description: "Nachweise, an denen noch eine Rückfrage hängt.",
    group: "Allgemein",
  },
  "billing-missing-numbers": {
    label: "Kontakte ohne Nummer",
    description: "Kunden ohne Kundennummer und Lieferanten ohne Lieferantennummer. Die Nummer lässt sich direkt eintragen.",
    group: "Allgemein",
  },
  "office-points": {
    label: "Offene Punkte",
    description: "Zähler für Material, Planung, Angebote, Mahnungen und Anfragen.",
    group: "Allgemein",
  },
  orders: {
    label: "Letzte Aufträge",
    description: "Die fünf zuletzt angelegten Aufträge.",
    group: "Allgemein",
  },
  "personal-overview": {
    label: "Mein Überblick",
    description: "Stundenkonto, Resturlaub, Stunden im Monat und ein laufender Timer.",
    group: "Personal",
  },
  "personal-absences": {
    label: "Meine Abwesenheiten",
    description: "Deine genehmigten und beantragten Abwesenheiten ab heute.",
    group: "Personal",
  },
  "team-absences": {
    label: "Heute abwesend",
    description: "Wer im Team heute fehlt und was in den nächsten Tagen ansteht.",
    group: "Personal",
  },
  subcontractors: {
    label: "Nachunternehmer",
    description: "Offene, noch nicht bezahlte Zeiten der Nachunternehmer.",
    group: "Personal",
  },
  "bw-material-open": {
    label: "Offene Materialanforderungen",
    description: "Anforderungen, die noch niemand in Bearbeitung genommen hat.",
    group: "Bestellwesen",
  },
  "bw-material-work": {
    label: "Material in Bearbeitung",
    description: "Materialanforderungen, die gerade bearbeitet werden.",
    group: "Bestellwesen",
  },
  "bw-inquiries": {
    label: "Offene Anfragen",
    description: "Lieferantenanfragen, die gesendet oder bereit sind und noch keine Bestellung haben.",
    group: "Bestellwesen",
  },
  "bw-orders-late": {
    label: "Überfällige Bestellungen",
    description: "Bestellungen, die lange offen sind und noch nicht als geliefert gelten.",
    group: "Bestellwesen",
  },
};

export const HOME_TILE_LABELS: Record<HomeTileId, string> = {
  kpis: HOME_TILE_INFO.kpis.label,
  todos: HOME_TILE_INFO.todos.label,
  mentions: HOME_TILE_INFO.mentions.label,
  absences: HOME_TILE_INFO.absences.label,
  "my-day": HOME_TILE_INFO["my-day"].label,
  material: HOME_TILE_INFO.material.label,
  completions: HOME_TILE_INFO.completions.label,
  lieferscheine: HOME_TILE_INFO.lieferscheine.label,
  angebote: HOME_TILE_INFO.angebote.label,
  rueckfragen: HOME_TILE_INFO.rueckfragen.label,
  "billing-signed": HOME_TILE_INFO["billing-signed"].label,
  "billing-lieferscheine": HOME_TILE_INFO["billing-lieferscheine"].label,
  "billing-drafts": HOME_TILE_INFO["billing-drafts"].label,
  "billing-questions": HOME_TILE_INFO["billing-questions"].label,
  "billing-missing-numbers": HOME_TILE_INFO["billing-missing-numbers"].label,
  "office-points": HOME_TILE_INFO["office-points"].label,
  orders: HOME_TILE_INFO.orders.label,
  "personal-overview": HOME_TILE_INFO["personal-overview"].label,
  "personal-absences": HOME_TILE_INFO["personal-absences"].label,
  "team-absences": HOME_TILE_INFO["team-absences"].label,
  subcontractors: HOME_TILE_INFO.subcontractors.label,
  "bw-material-open": HOME_TILE_INFO["bw-material-open"].label,
  "bw-material-work": HOME_TILE_INFO["bw-material-work"].label,
  "bw-inquiries": HOME_TILE_INFO["bw-inquiries"].label,
  "bw-orders-late": HOME_TILE_INFO["bw-orders-late"].label,
};

export interface HomeLayoutSettings {
  order: string[];
  columns: HomeColumnCount;
  spans: Partial<Record<HomeTileId, HomeTileSpan>>;
  hidden: string[];
  added: string[];
}

export const DEFAULT_HOME_LAYOUT: HomeLayoutSettings = {
  order: [],
  columns: 2,
  spans: {},
  hidden: [],
  added: [],
};

const HOME_TILE_ID_SET = new Set<string>(HOME_TILE_IDS);

const HALF_WIDTH_TILES = new Set<HomeTileId>([
  "todos",
  "mentions",
  "completions",
  "lieferscheine",
  "angebote",
  "rueckfragen",
  "billing-signed",
  "billing-lieferscheine",
  "billing-drafts",
  "billing-questions",
  "billing-missing-numbers",
  "office-points",
  "orders",
  "personal-absences",
  "team-absences",
  "subcontractors",
  "bw-material-open",
  "bw-material-work",
  "bw-inquiries",
  "bw-orders-late",
]);

export function isHomeTileId(value: string): value is HomeTileId {
  return HOME_TILE_ID_SET.has(value);
}

export function homeTileSpan(id: HomeTileId, ordersFullWidth: boolean): "full" | "half" {
  if (id === "orders" && ordersFullWidth) return "full";
  return HALF_WIDTH_TILES.has(id) ? "half" : "full";
}

export function isHomeColumnCount(value: unknown): value is HomeColumnCount {
  return value === 1 || value === 2 || value === 3;
}

export function isHomeTileSpan(value: unknown): value is HomeTileSpan {
  return value === 1 || value === 2 || value === 3;
}

function uniqueTileIds(values: readonly string[] | null | undefined): string[] {
  const result: string[] = [];
  const seen = new Set<string>();
  for (const raw of values ?? []) {
    if (!isHomeTileId(raw) || seen.has(raw)) continue;
    result.push(raw);
    seen.add(raw);
  }
  return result;
}

export function normalizeHomeLayout(
  raw: Partial<HomeLayoutSettings> | null | undefined,
): HomeLayoutSettings {
  const hidden = uniqueTileIds(raw?.hidden);
  const hiddenSet = new Set(hidden);
  const spans: Partial<Record<HomeTileId, HomeTileSpan>> = {};
  const rawSpans = raw?.spans;
  if (rawSpans && typeof rawSpans === "object") {
    for (const [key, value] of Object.entries(rawSpans)) {
      if (!isHomeTileId(key) || !isHomeTileSpan(value)) continue;
      spans[key] = value;
    }
  }
  return {
    order: uniqueTileIds(raw?.order),
    columns: isHomeColumnCount(raw?.columns) ? raw.columns : 2,
    spans,
    hidden,
    added: uniqueTileIds(raw?.added).filter((id) => !hiddenSet.has(id)),
  };
}

/** Breite einer Kachel im gewählten Spaltenraster. Gespeicherte Werte über der Spaltenzahl werden gekappt. */
export function resolveHomeTileSpan(
  id: HomeTileId,
  columns: HomeColumnCount,
  spans: HomeLayoutSettings["spans"],
  ordersFullWidth: boolean,
): HomeTileSpan {
  const stored = spans[id];
  const fallback: HomeTileSpan = homeTileSpan(id, ordersFullWidth) === "full" ? columns : 1;
  const chosen = isHomeTileSpan(stored) ? stored : fallback;
  return (chosen > columns ? columns : chosen) as HomeTileSpan;
}

/**
 * Sichtbare Kacheln: Standard abzüglich ausgeblendeter, plus ausdrücklich
 * hinzugefügte. Kacheln ohne Berechtigung fallen weg.
 */
export function visibleHomeTileIds(
  permitted: readonly HomeTileId[],
  defaults: readonly HomeTileId[],
  layout: HomeLayoutSettings,
): HomeTileId[] {
  const permittedSet = new Set(permitted);
  const hidden = new Set(layout.hidden.filter((id) => isHomeTileId(id) && permittedSet.has(id)));
  const base: HomeTileId[] = [];
  const seen = new Set<HomeTileId>();
  for (const id of defaults) {
    if (!permittedSet.has(id) || hidden.has(id) || seen.has(id)) continue;
    base.push(id);
    seen.add(id);
  }
  for (const raw of layout.added) {
    if (!isHomeTileId(raw) || !permittedSet.has(raw) || hidden.has(raw) || seen.has(raw)) continue;
    base.push(raw);
    seen.add(raw);
  }
  return mergeHomeTileOrder(base, layout.order);
}

export function hideHomeTile(
  layout: HomeLayoutSettings,
  id: HomeTileId,
  permitted: readonly HomeTileId[],
  defaults: readonly HomeTileId[],
): HomeLayoutSettings {
  const visible = visibleHomeTileIds(permitted, defaults, layout).filter((tile) => tile !== id);
  return {
    ...layout,
    hidden: uniqueTileIds([...layout.hidden, id]),
    added: layout.added.filter((tile) => tile !== id),
    order: weaveHomeOrder(layout.order, visible),
  };
}

export function showHomeTile(
  layout: HomeLayoutSettings,
  id: HomeTileId,
  permitted: readonly HomeTileId[],
  defaults: readonly HomeTileId[],
): HomeLayoutSettings {
  if (!permitted.includes(id)) return layout;
  const before = visibleHomeTileIds(permitted, defaults, layout);
  if (before.includes(id)) return layout;
  const defaultSet = new Set(defaults);
  const nextLayout: HomeLayoutSettings = {
    ...layout,
    hidden: layout.hidden.filter((tile) => tile !== id),
    added: defaultSet.has(id) ? layout.added.filter((tile) => tile !== id) : uniqueTileIds([...layout.added, id]),
  };
  const visible = visibleHomeTileIds(permitted, defaults, nextLayout).filter((tile) => tile !== id);
  visible.push(id);
  return {
    ...nextLayout,
    order: weaveHomeOrder(layout.order, visible),
  };
}

export function setHomeColumns(
  layout: HomeLayoutSettings,
  columns: HomeColumnCount,
): HomeLayoutSettings {
  const spans: Partial<Record<HomeTileId, HomeTileSpan>> = {};
  for (const [key, value] of Object.entries(layout.spans)) {
    if (!isHomeTileId(key) || !isHomeTileSpan(value)) continue;
    spans[key] = (value > columns ? columns : value) as HomeTileSpan;
  }
  return { ...layout, columns, spans };
}

export function setHomeTileSpan(
  layout: HomeLayoutSettings,
  id: HomeTileId,
  span: HomeTileSpan,
): HomeLayoutSettings {
  return { ...layout, spans: { ...layout.spans, [id]: span } };
}

/** Sichtbare Kacheln in gespeicherter Reihenfolge, neue Kacheln an der Standardposition. */
export function mergeHomeTileOrder(
  visible: HomeTileId[],
  saved: string[] | null | undefined,
): HomeTileId[] {
  const visibleSet = new Set(visible);
  const defaultOrder = HOME_TILE_IDS.filter((id) => visibleSet.has(id));
  if (!saved?.length) return defaultOrder;

  const placed = new Set<HomeTileId>();
  const result: HomeTileId[] = [];
  for (const raw of saved) {
    if (!isHomeTileId(raw) || !visibleSet.has(raw) || placed.has(raw)) continue;
    result.push(raw);
    placed.add(raw);
  }

  for (let i = 0; i < defaultOrder.length; i += 1) {
    const id = defaultOrder[i];
    if (id == null || placed.has(id)) continue;
    let insertAt = 0;
    for (let j = i - 1; j >= 0; j -= 1) {
      const previous = defaultOrder[j];
      if (previous == null) continue;
      const prevIndex = result.indexOf(previous);
      if (prevIndex >= 0) {
        insertAt = prevIndex + 1;
        break;
      }
    }
    result.splice(insertAt, 0, id);
    placed.add(id);
  }
  return result;
}

/**
 * Schreibt die neue Reihenfolge der sichtbaren Kacheln in die gespeicherte Liste
 * und lässt Kacheln anderer Ansichten an ihrer Stelle.
 */
export function weaveHomeOrder(saved: string[], nextVisible: HomeTileId[]): string[] {
  const visibleSet = new Set<string>(nextVisible);
  const queue = [...nextVisible];
  const used = new Set<string>();
  const result: string[] = [];

  for (const raw of saved) {
    if (visibleSet.has(raw)) {
      const next = queue.shift();
      if (next && !used.has(next)) {
        result.push(next);
        used.add(next);
      }
      continue;
    }
    if (raw && !used.has(raw)) {
      result.push(raw);
      used.add(raw);
    }
  }

  for (const id of queue) {
    if (!used.has(id)) {
      result.push(id);
      used.add(id);
    }
  }
  return result;
}

export async function getHomeLayout(): Promise<HomeLayoutSettings> {
  const res = await api.get<Partial<HomeLayoutSettings>>("/me/home-layout");
  return normalizeHomeLayout(res);
}

export async function saveHomeLayout(layout: HomeLayoutSettings): Promise<HomeLayoutSettings> {
  const payload = normalizeHomeLayout(layout);
  const res = await api.put<Partial<HomeLayoutSettings>>("/me/home-layout", payload);
  return normalizeHomeLayout(res);
}
